import { useCallback, useEffect, useRef, useState } from "react";

export interface StickyHeaderOptions {
    headerRef: React.RefObject<HTMLTableSectionElement>;
    wrapperRef: React.RefObject<HTMLDivElement>;
    stickyHeaderRef: React.RefObject<HTMLDivElement>;
    stickyTopOffset?: number;
    disabled?: boolean;
}

export interface StickyHeaderReturn {
    isVisible: boolean;
    offsetTop: number;
}

const PLATFORM_HEADER_SELECTOR = ".PlatformHeader";
const BROWSE_SECTION_SELECTOR = ".StickyBrowseSection__fixed";
const BROWSE_SECTION_STICKY_CLASS = "is-visible";
const DEFAULT_OFFSET = 250;
const INTERSECTION_THRESHOLDS = [0, 0.1, 0.5, 1];

export default function useStickyHeader({
    headerRef,
    wrapperRef,
    stickyHeaderRef,
    stickyTopOffset,
    disabled = false
}: StickyHeaderOptions): StickyHeaderReturn {
    const [isVisible, setIsVisible] = useState(false);
    const [offsetTop, setOffsetTop] = useState<number>(0);
    const hasEnteredViewportOnce = useRef(false);
    const tableInViewRef = useRef(true);

    const getPlatformElements = useCallback(() => {
        if (disabled) return { platformHeader: null, browseSection: null };
        const platformHeader = document.querySelector<HTMLElement>(PLATFORM_HEADER_SELECTOR);
        const browseSection = document.querySelector<HTMLElement>(BROWSE_SECTION_SELECTOR);
        return { platformHeader, browseSection };
    }, [disabled]);

    const calculateOffsetFromHeaders = useCallback((): number => {
        if (disabled) return 0;
        const { platformHeader, browseSection } = getPlatformElements();
        if (!platformHeader || !browseSection) return DEFAULT_OFFSET;

        const headerRect = platformHeader.getBoundingClientRect();
        const browseSectionRect = browseSection.getBoundingClientRect();
        return headerRect.bottom + browseSectionRect.height;
    }, [disabled, getPlatformElements]);

    const updateTableWidth = useCallback(() => {
        if (disabled) return;
        const wrapper = wrapperRef.current;
        const sticky = stickyHeaderRef.current;
        if (!wrapper || !sticky) return;

        const availableWidth = wrapper.getBoundingClientRect().width;
        const scrollbarWidth = wrapper.offsetWidth - wrapper.clientWidth;
        const tableWidth = availableWidth - scrollbarWidth;
        
        sticky.style.width = `${tableWidth}px`;
        sticky.style.left = `${wrapper.getBoundingClientRect().left}px`;
    }, [disabled, wrapperRef, stickyHeaderRef]);

    const updateHeaderCellWidths = useCallback(() => {
        if (disabled) return;
        const sourceHeader = headerRef.current;
        const stickyHeader = stickyHeaderRef.current;
        if (!sourceHeader || !stickyHeader) return;

        const sourceCells = sourceHeader.querySelectorAll<HTMLTableCellElement>("th");
        const targetCells = stickyHeader.querySelectorAll<HTMLTableCellElement>("th");

        sourceCells.forEach((sourceCell, index) => {
            const cellWidth = sourceCell.getBoundingClientRect().width;
            const targetCell = targetCells[index];
            if (targetCell && cellWidth > 0) {
                targetCell.style.width = `${cellWidth}px`;
            }
        });
    }, [disabled, headerRef, stickyHeaderRef]);

    useEffect(() => {
        if (disabled) {
            setIsVisible(false);
            setOffsetTop(0);
            return;
        }

        const updateHeaderOffset = () => {
            setOffsetTop(calculateOffsetFromHeaders());
        };

        window.addEventListener("resize", updateHeaderOffset);
        updateHeaderOffset();
        return () => window.removeEventListener("resize", updateHeaderOffset);
    }, [disabled, calculateOffsetFromHeaders]);

    useEffect(() => {
        if (disabled) return;

        const header = headerRef.current;
        const { platformHeader, browseSection } = getPlatformElements();
        if (!header || !platformHeader || !browseSection) return;

        const platformHeaderHeight = platformHeader.getBoundingClientRect().bottom;

        const shouldShowStickyHeader = () => {
            if (!header) return false;
            
            const headerPosition = header.getBoundingClientRect();
            const isBrowseSectionSticky = browseSection.classList.contains(BROWSE_SECTION_STICKY_CLASS);
            const isHeaderBelowPlatform = headerPosition.top <= platformHeaderHeight;
            
            return hasEnteredViewportOnce.current && 
                   isHeaderBelowPlatform && 
                   tableInViewRef.current && 
                   isBrowseSectionSticky;
        };

        const updateStickyState = () => {
            requestAnimationFrame(() => setIsVisible(shouldShowStickyHeader()));
        };

        const browseSectionObserver = new MutationObserver(updateStickyState);
        browseSectionObserver.observe(browseSection, {
            attributes: true,
            attributeFilter: ['class']
        });

        const headerObserver = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    hasEnteredViewportOnce.current = true;
                }
                updateStickyState();
            },
            { 
                root: null, 
                threshold: INTERSECTION_THRESHOLDS, 
                rootMargin: `-${platformHeaderHeight}px 0px 0px 0px` 
            }
        );
        headerObserver.observe(header);

        window.addEventListener('scroll', updateStickyState, { passive: true });

        return () => {
            headerObserver.disconnect();
            browseSectionObserver.disconnect();
            window.removeEventListener('scroll', updateStickyState);
        };
    }, [disabled, headerRef, getPlatformElements]);

    useEffect(() => {
        if (disabled) return;

        const wrapper = wrapperRef.current;
        if (!wrapper) return;

        const tableVisibilityObserver = new IntersectionObserver(([entry]) => {
            tableInViewRef.current = entry.isIntersecting;
            if (!entry.isIntersecting) setIsVisible(false);
        });

        tableVisibilityObserver.observe(wrapper);
        return () => tableVisibilityObserver.disconnect();
    }, [disabled, wrapperRef]);

    useEffect(() => {
        if (disabled) return;

        const wrapper = wrapperRef.current;
        const sticky = stickyHeaderRef.current;
        if (!wrapper || !sticky) return;

        const syncScrollPosition = () => {
            sticky.scrollLeft = Math.round(wrapper.scrollLeft);
        };

        const handleWrapperScroll = () => syncScrollPosition();
        const handleStickyScroll = () => {
            wrapper.scrollLeft = sticky.scrollLeft;
        };

        const resizeObserver = new ResizeObserver(() => {
            requestAnimationFrame(() => {
                updateTableWidth();
                syncScrollPosition();
            });
        });
        resizeObserver.observe(wrapper);

        wrapper.addEventListener("scroll", handleWrapperScroll, { passive: true });
        sticky.addEventListener("scroll", handleStickyScroll, { passive: true });

        updateTableWidth();
        syncScrollPosition();

        return () => {
            wrapper.removeEventListener("scroll", handleWrapperScroll);
            sticky.removeEventListener("scroll", handleStickyScroll);
            resizeObserver.disconnect();
        };
    }, [disabled, wrapperRef, stickyHeaderRef, updateTableWidth]);

    useEffect(() => {
        if (disabled) return;

        const stickyHeader = stickyHeaderRef.current;
        const wrapper = wrapperRef.current;
        if (!stickyHeader || !wrapper) return;

        const getHorizontalScrollAmount = (event: WheelEvent): number => {
            if (event.deltaX !== 0) return event.deltaX;
            if (event.shiftKey) return event.deltaY;
            return 0;
        };

        const handleHorizontalScroll = (event: WheelEvent) => {
            const scrollAmount = getHorizontalScrollAmount(event);
            if (scrollAmount !== 0) {
                event.preventDefault();
                wrapper.scrollLeft += scrollAmount;
                stickyHeader.scrollLeft = wrapper.scrollLeft;
            }
        };

        stickyHeader.addEventListener("wheel", handleHorizontalScroll, { passive: false });
        return () => stickyHeader.removeEventListener("wheel", handleHorizontalScroll);
    }, [disabled, wrapperRef, stickyHeaderRef]);

    useEffect(() => {
        if (disabled) return;

        const resizeObserver = new ResizeObserver(updateHeaderCellWidths);
        const header = headerRef.current;
        
        if (header) {
            resizeObserver.observe(header);
            updateHeaderCellWidths();
        }

        window.addEventListener("resize", updateHeaderCellWidths);
        
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener("resize", updateHeaderCellWidths);
        };
    }, [disabled, updateHeaderCellWidths, headerRef]);

    return {
        isVisible: disabled ? false : isVisible,
        offsetTop: disabled ? 0 : offsetTop
    };
} 